/**
 * Creates a debounced version of a function.
 *
 * @param fn The function to debounce
 * @param wait The time in milliseconds to delay
 * @param maxWait Optional upper bound on how long a continuous stream of calls can be
 *   delayed. Even if the caller never pauses for `wait`, the function still fires once
 *   `maxWait` ms have elapsed since the first call of the current burst. Omit for the
 *   classic trailing-only behavior.
 * @returns A debounced version of the input function with the same signature
 */
export default function debounce<T extends (...args: any[]) => any>(fn: T, wait: number, maxWait?: number): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	let firstCallTime: number | null = null;

	return function (this: any, ...args: Parameters<T>): void {
		const context = this;

		const invoke = (): void => {
			if (timeoutId != null) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
			firstCallTime = null;
			fn.apply(context, args);
		};

		if (timeoutId != null) {
			clearTimeout(timeoutId);
		}

		if (maxWait != null) {
			const now = Date.now();
			firstCallTime ??= now;
			const remainingMax = firstCallTime + maxWait - now;
			if (remainingMax <= 0) {
				invoke();
				return;
			}
			timeoutId = setTimeout(invoke, Math.min(wait, remainingMax));
			return;
		}

		timeoutId = setTimeout(() => {
			fn.apply(context, args);
			timeoutId = null;
		}, wait);
	};
}
